import { Component, effect, inject, input, output, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import {
  CellClickEvent,
  CellTemplateDirective,
  ColumnComponent,
  FilterCellTemplateDirective,
  GridComponent,
  GridDataResult,
  LoadingTemplateDirective,
  NoRecordsTemplateDirective,
} from '@progress/kendo-angular-grid';
import { debounceTime, finalize, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  CapitalizePipe,
  clearCustomFilters,
  ClientOrder,
  ClientOrderItem,
  getParamValue,
  HydraCollectionResponse,
  KendoGridComponent,
  ListSkeletonComponent,
  onPageChange,
  setCustomFilter,
  SupplierInvoiceItem,
  SupplierInvoiceSection,
  AMOUNT_FORMAT,
} from 'shared';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from '@environments';
import { State } from '@progress/kendo-data-query';
import { CheckBoxComponent, CheckBoxState, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';

@Component({
  selector: 'rc-client-order-item-table',
  imports: [
    CapitalizePipe,
    CellTemplateDirective,
    CheckBoxComponent,
    ColumnComponent,
    FilterCellTemplateDirective,
    GridComponent,
    ListSkeletonComponent,
    LoadingTemplateDirective,
    NoRecordsTemplateDirective,
    TextBoxComponent,
    TooltipDirective,
    TranslateModule,
    NumberPipe,
  ],
  templateUrl: './client-order-item-table.component.html',
  styleUrl: './client-order-item-table.component.scss',
})
export class ClientOrderItemTableComponent extends KendoGridComponent {
  #httpClient = inject(HttpClient);
  dialogService = inject(DialogService);
  dialog: DialogRef;

  gridData$: GridDataResult = { total: 0, data: [] };
  clientOrderItemSelection = output<ClientOrderItem | undefined>();

  supplierInvoiceItem = input.required<SupplierInvoiceItem>();
  section = input.required<SupplierInvoiceSection>();
  clientOrder = input.required<ClientOrder>();
  itemsPerPage = input<number>(5);
  params = signal<string[]>([]);
  selectedOrderItem = input<ClientOrderItem | undefined>(undefined);
  paramString = signal<string>('');
  initialValue = input<string>();
  hasAppliedInitial = signal(false);
  amountShown = signal<{ current: number; total: number }>({ current: 0, total: 0 });

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;

  private destroy$ = new Subject<void>();
  private lastKey = '';
  private gridInitialized = false;
  private prevParamsKey = '';

  reload$ = new Subject<void>();

  constructor() {
    super();
    this.isFrontendFilterTable.set(true);
    effect(() => {
      const gridInstance = this.grid();
      const settings = this.userSettings();
      if (!gridInstance || !settings) return;
    });
    effect(() => {
      const ref = this.section()?.reference;
      this.params.set([`reference=${ref}`]);
    });

    effect(() => {
      const key = this.params().join('&');

      const initVal = this.initialValue();
      const sel = this.selectedOrderItem();

      // const waitingForInitial = !!initVal && (!sel || (sel && initVal === sel.articleNumber && !this.hasAppliedInitial()));
      //
      // if (waitingForInitial) return;

      if (key && key !== this.prevParamsKey) {
        this.prevParamsKey = key;
        this.reload$.next();
      }
    });

    this.reload$
      .pipe(
        debounceTime(300),
        map(() => this.params().join('&')),
        distinctUntilChanged(),
        tap((key) => (this.lastKey = key)),
        switchMap(() => this.#getClientOrderItems()),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => (this.gridData$ = value));

    effect(() => {
      const selection = this.selectedOrderItem();
      const initialValue = this.initialValue();
      const pageSize = this.itemsPerPage();

      this.params.update((prev) => {
        const upsert = (arr: string[], key: string, value: string) => {
          const entry = `${key}=${value}`;
          const i = arr.findIndex((p) => p.startsWith(`${key}=`));
          if (i === -1) return [...arr, entry];
          if (arr[i] === entry) return arr; // no change
          const next = arr.slice();
          next[i] = entry;
          return next;
        };

        let next = upsert(prev, 'itemsPerPage', String(pageSize));

        if (!this.hasAppliedInitial() && initialValue) {
          next = upsert(next, 'article.normalizedNumber', initialValue);
          this.hasAppliedInitial.set(true);
        }

        const nextString = next.join('&');
        if (nextString !== this.paramString()) {
          this.paramString.set(nextString);
        }

        return next;
      });
    });
  }

  #getClientOrderItems(): Observable<GridDataResult> {
    this.isLoadingGridData$.next(true);
    const paramString = this.params().join('&');
    return this.#httpClient
      .get<
        HydraCollectionResponse<ClientOrderItem>
      >(`${environment.apiUrl}${this.clientOrder()!['@id']}/client_order_items?${paramString}`)
      .pipe(
        map((response) => ({
          total: response['hydra:totalItems'],
          data: [...response['hydra:member']],
        })),
        tap((result) => {
          this.amountShown.set({ current: result.data.length, total: result.total });
        }),
        finalize(() => this.isLoadingGridData$.next(false)),
      );
  }

  onDataStateChange(state: State): void {
    this.gridState = state;
    if (!this.gridInitialized) {
      this.gridInitialized = true;
      return;
    }

    this.reload$.next();
  }

  onGridRowClick(event: CellClickEvent): void {
    if (this.hasClickedGridRowButtons(event) || !this.isLeftClick(event)) {
      return;
    }

    const item = event.dataItem as ClientOrderItem;
    const currentlySelected = this.selectedOrderItem()?.id === item.id;

    if (currentlySelected) {
      // unselect
      this.clientOrderItemSelection.emit(undefined);
      clearCustomFilters(this.itemsPerPage(), this.params, this.reload$);
    } else {
      // select
      this.clientOrderItemSelection.emit(item);
    }
  }

  removeOrSetSelection(event: CheckBoxState, item: ClientOrderItem) {
    if (event === true) {
      this.clientOrderItemSelection.emit(item);
    } else {
      if (item['@id'] === this.selectedOrderItem()?.['@id']) {
        this.clientOrderItemSelection.emit(undefined);
        clearCustomFilters(this.itemsPerPage(), this.params, this.reload$);
      }
    }
  }

  protected readonly onPageChange = onPageChange;
  protected readonly clearCustomFilters = clearCustomFilters;
  protected readonly setCustomFilter = setCustomFilter;
  protected readonly getParamValue = getParamValue;
}
